import type {
  AlertDetail,
  AlertListItem,
  AlertTarget,
  AlertTrigger,
} from "../domain";
import type {
  AlertDetailDto,
  AlertListItemDto,
  AlertTargetDto,
  AlertTriggerDto,
} from "./alertDtos";

/** Drops the keys left null or undefined, so only the category's own
 *  trigger fields remain. */
function present<T extends object>(value: T): { [K in keyof T]?: NonNullable<T[K]> } {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== null && entry !== undefined),
  ) as { [K in keyof T]?: NonNullable<T[K]> };
}

export function alertTriggerFromDto(dto: AlertTriggerDto | null | undefined): AlertTrigger {
  return dto === null || dto === undefined ? {} : present(dto);
}

export function alertTargetFromDto(dto: AlertTargetDto): AlertTarget {
  return {
    id: dto.id,
    assetType: dto.assetType ?? null,
    asset:
      dto.asset === null || dto.asset === undefined
        ? null
        : { ...dto.asset, code: dto.asset.code ?? null },
    deviceType: dto.deviceType ?? null,
    device:
      dto.device === null || dto.device === undefined
        ? null
        : {
            id: dto.device.id,
            imei: dto.device.imei ?? null,
            nameEn: dto.device.nameEn ?? null,
            nameAr: dto.device.nameAr ?? null,
          },
    geofence: dto.geofence ?? null,
  };
}

export function alertListItemFromDto(dto: AlertListItemDto): AlertListItem {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    priority: dto.priority,
    status: dto.status,
    targetType: dto.targetType,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    account: dto.account,
    category: dto.category,
  };
}

export function alertDetailFromDto(dto: AlertDetailDto): AlertDetail {
  const targets = (dto.targets ?? []).map(alertTargetFromDto);
  const recipients = (dto.recipients ?? []).map((row) => row.user);
  const watchedAssets = (dto.watchedAssets ?? []).map((asset) => ({
    ...asset,
    code: asset.code ?? null,
  }));
  return {
    ...alertListItemFromDto(dto),
    description: dto.description ?? null,
    accountId: dto.accountId,
    trigger: alertTriggerFromDto(dto.trigger),
    targets,
    targetIds: dto.targetIds ?? targets.map(targetValueId),
    watchedAssets,
    assetIds: dto.assetIds ?? watchedAssets.map((asset) => asset.id),
    recipients,
    recipientIds: dto.recipientIds ?? recipients.map((recipient) => recipient.id),
    channels: dto.channels ?? [],
    alwaysActive: dto.alwaysActive,
    activeDays: dto.activeDays ?? [],
    repeatWeekly: dto.repeatWeekly ?? false,
    startTime: dto.startTime ?? null,
    endTime: dto.endTime ?? null,
    startDate: dto.startDate ?? null,
    endDate: dto.endDate ?? null,
  };
}

/** The id of the thing a target row points at (not the row's own id). */
export function targetValueId(target: AlertTarget): string {
  return (
    target.assetType?.id ??
    target.asset?.id ??
    target.deviceType?.id ??
    target.device?.id ??
    target.geofence?.id ??
    target.id
  );
}
